import React from 'react';
import Chip from '@material-ui/core/Chip';
import '../assets/scss/_chip-styling.scss';

// Helper to extract all applied filter chips from filterState
function getActiveChips(filterState) {
  if (!filterState) return [];
  const chips = [];

  // Tags (multi)
  if (filterState.multi?.active && Array.isArray(filterState.multi.data)) {
    filterState.multi.data.forEach(tag => {
      if (tag.label && tag.value)
        chips.push({ type: 'tags', label: tag.label, value: tag.value });
    });
  }
  // Tags2 (Required Elements)
  if (filterState.tags2?.active && Array.isArray(filterState.tags2.data)) {
    filterState.tags2.data.forEach(tag => {
      if (tag.label && tag.value)
        chips.push({ type: 'tags2', label: tag.label, value: tag.value });
    });
  }
  // Genres
  if (filterState.genres?.active && Array.isArray(filterState.genres.data)) {
    filterState.genres.data.forEach(genre => {
      if (genre.label && genre.value)
        chips.push({ type: 'genres', label: genre.label, value: genre.value });
    });
  }
  // BPM
  if (filterState.bpm?.active) {
    if (filterState.bpm.filterType === 'range' && (filterState.bpm.range?.min || filterState.bpm.range?.max)) {
      const min = filterState.bpm.range?.min;
      const max = filterState.bpm.range?.max;
      
      let label = '';
      if (min && max) {
        // Complete range
        label = `${min}-${max} bpm`;
      } else if (min && !max) {
        // Minimum only
        label = `${min}+ bpm`;
      } else if (!min && max) {
        // Maximum only
        label = `<${max} bpm`;
      }
      
      if (label) {
        chips.push({ type: 'bpm', label: label, value: { min: min || '', max: max || '' } });
      }
    } else if (filterState.bpm.exact) {
      // If exact is an object with label or value, use that
      let bpmLabel = '';
      if (typeof filterState.bpm.exact === 'object' && filterState.bpm.exact !== null) {
        bpmLabel = filterState.bpm.exact.label || filterState.bpm.exact.value || '';
      } else {
        bpmLabel = filterState.bpm.exact;
      }
      chips.push({ type: 'bpm', label: `${bpmLabel} bpm`, value: filterState.bpm.exact });
    }
  }
  // Keys
  if (filterState.keys?.active && Array.isArray(filterState.keys.data)) {
    filterState.keys.data.forEach(key => {
      if (key.label && key.value)
        chips.push({ type: 'keys', label: key.label, value: key.value });
    });
  }
  // Studios
  if (filterState.studios?.active && Array.isArray(filterState.studios.data)) {
    filterState.studios.data.forEach(studio => {
      if (studio.label && studio.value)
        chips.push({ type: 'studios', label: studio.label, value: studio.value });
    });
  }
  // Agencies
  if (filterState.agencies?.active && Array.isArray(filterState.agencies.data)) {
    filterState.agencies.data.forEach(agency => {
      if (agency.label && agency.value)
        chips.push({ type: 'agencies', label: agency.label, value: agency.value });
    });
  }
  // Clients
  if (filterState.clients?.active && Array.isArray(filterState.clients.data)) {
    filterState.clients.data.forEach(client => {
      if (client.label && client.value)
        chips.push({ type: 'clients', label: client.label, value: client.value });
    });
  }
  // Producers
  if (filterState.producers?.active && Array.isArray(filterState.producers.data)) {
    filterState.producers.data.forEach(producer => {
      if (producer.label && producer.value)
        chips.push({ type: 'producers', label: producer.label, value: producer.value });
    });
  }
  // Composers
  if (filterState.composers?.active && Array.isArray(filterState.composers.data)) {
    filterState.composers.data.forEach(composer => {
      if (composer.label && composer.value)
        chips.push({ type: 'composers', label: composer.label, value: composer.value });
    });
  }
  // Creative Directors
  if (filterState.creativeDirectors?.active && Array.isArray(filterState.creativeDirectors.data)) {
    filterState.creativeDirectors.data.forEach(cd => {
      if (cd.label && cd.value)
        chips.push({ type: 'creativeDirectors', label: cd.label, value: cd.value });
    });
  }
  // Session Artists
  if (filterState.sessionArtists?.active && Array.isArray(filterState.sessionArtists.data)) {
    filterState.sessionArtists.data.forEach(sa => {
      if (sa.label && sa.value)
        chips.push({ type: 'sessionArtists', label: sa.label, value: sa.value });
    });
  }
  // License
  if (filterState.license?.active && filterState.license.value !== undefined) {
    chips.push({ type: 'license', label: filterState.license.value ? 'Licensed' : 'Unlicensed', value: filterState.license.value });
  }
  // Date
  if (filterState.date?.active && (filterState.date.from || filterState.date.to)) {
    let label = '';
    if (filterState.date.from && filterState.date.to) {
      label = `${new Date(filterState.date.from).toLocaleDateString()} - ${new Date(filterState.date.to).toLocaleDateString()}`;
    } else if (filterState.date.from) {
      label = `From ${new Date(filterState.date.from).toLocaleDateString()}`;
    } else if (filterState.date.to) {
      label = `To ${new Date(filterState.date.to).toLocaleDateString()}`;
    }
    chips.push({ type: 'date', label, value: { from: filterState.date.from, to: filterState.date.to } });
  }
  // Text search
  if (filterState.text?.active && filterState.text.value) {
    chips.push({ type: 'text', label: filterState.text.value, value: filterState.text.value });
  }
  
  // Excluded Tags
  if (filterState.excludedTags?.active && Array.isArray(filterState.excludedTags.data)) {
    filterState.excludedTags.data.forEach(tag => {
      if (tag.label && tag.value)
        chips.push({ type: 'excludedTags', label: tag.label, value: tag.value, excluded: true });
    });
  }
  
  // Excluded Tags2
  if (filterState.excludedTags2?.active && Array.isArray(filterState.excludedTags2.data)) {
    filterState.excludedTags2.data.forEach(tag => {
      if (tag.label && tag.value)
        chips.push({ type: 'excludedTags2', label: tag.label, value: tag.value, excluded: true });
    });
  }
  
  // Excluded Genres
  if (filterState.excludedGenres?.active && Array.isArray(filterState.excludedGenres.data)) {
    filterState.excludedGenres.data.forEach(genre => {
      if (genre.label && genre.value)
        chips.push({ type: 'excludedGenres', label: genre.label, value: genre.value, excluded: true });
    });
  }
  
  // Excluded Keys
  if (filterState.excludedKeys?.active && Array.isArray(filterState.excludedKeys.data)) {
    filterState.excludedKeys.data.forEach(key => {
      if (key.label && key.value)
        chips.push({ type: 'excludedKeys', label: key.label, value: key.value, excluded: true });
    });
  }
  
  // Excluded Studios
  if (filterState.excludedStudios?.active && Array.isArray(filterState.excludedStudios.data)) {
    filterState.excludedStudios.data.forEach(studio => {
      if (studio.label && studio.value)
        chips.push({ type: 'excludedStudios', label: studio.label, value: studio.value, excluded: true });
    });
  }
  
  // Excluded Agencies
  if (filterState.excludedAgencies?.active && Array.isArray(filterState.excludedAgencies.data)) {
    filterState.excludedAgencies.data.forEach(agency => {
      if (agency.label && agency.value)
        chips.push({ type: 'excludedAgencies', label: agency.label, value: agency.value, excluded: true });
    });
  }
  
  // Excluded Clients
  if (filterState.excludedClients?.active && Array.isArray(filterState.excludedClients.data)) {
    filterState.excludedClients.data.forEach(client => {
      if (client.label && client.value)
        chips.push({ type: 'excludedClients', label: client.label, value: client.value, excluded: true });
    });
  }
  
  // Excluded Producers
  if (filterState.excludedProducers?.active && Array.isArray(filterState.excludedProducers.data)) {
    filterState.excludedProducers.data.forEach(producer => {
      if (producer.label && producer.value)
        chips.push({ type: 'excludedProducers', label: producer.label, value: producer.value, excluded: true });
    });
  }
  
  // Excluded Composers
  if (filterState.excludedComposers?.active && Array.isArray(filterState.excludedComposers.data)) {
    filterState.excludedComposers.data.forEach(composer => {
      if (composer.label && composer.value)
        chips.push({ type: 'excludedComposers', label: composer.label, value: composer.value, excluded: true });
    });
  }
  
  // Excluded Creative Directors
  if (filterState.excludedCreativeDirectors?.active && Array.isArray(filterState.excludedCreativeDirectors.data)) {
    filterState.excludedCreativeDirectors.data.forEach(cd => {
      if (cd.label && cd.value)
        chips.push({ type: 'excludedCreativeDirectors', label: cd.label, value: cd.value, excluded: true });
    });
  }
  
  // Excluded Session Artists
  if (filterState.excludedSessionArtists?.active && Array.isArray(filterState.excludedSessionArtists.data)) {
    filterState.excludedSessionArtists.data.forEach(sa => {
      if (sa.label && sa.value)
        chips.push({ type: 'excludedSessionArtists', label: sa.label, value: sa.value, excluded: true });
    });
  }
  
  return chips;
}

const ActiveFiltersBar = ({ filterState, onRemoveFilter, onClearAll, onToggleFilter }) => {
  const chips = getActiveChips(filterState);
  if (!chips.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '8px 0 16px 0', alignItems: 'center' }}>
      {chips.map((chip, idx) => (
        <Chip
          key={chip.type + '-' + chip.label + '-' + idx}
          label={chip.label}
          className={`custom-chip${chip.excluded ? ' excluded-chip' : ''}`}
          onClick={() => onToggleFilter && onToggleFilter(chip)}
          onDelete={() => onRemoveFilter(chip)}
          style={{ cursor: onToggleFilter ? 'pointer' : 'default' }}
        />
      ))}
      {/* Clear all button */}
      <button
        aria-label="Clear all filters"
        onClick={onClearAll}
        style={{
          background: 'none',
          border: 'none',
          color: '#fff',
          fontSize: 20,
          marginLeft: 8,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          padding: 0,
          height: 32
        }}
        title="Clear all filters"
      >
        <span style={{ fontSize: 24, lineHeight: 1 }}>×</span>
      </button>
    </div>
  );
};

export default ActiveFiltersBar; 